import {
  Fragment,
  type KeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import { Link, usePage } from '@inertiajs/react'
import {
  Archive,
  BriefcaseBusiness,
  ChevronRight,
  Download,
  ExternalLink,
  FolderClosed,
  FolderInput,
  GripVertical,
  Link2,
  MoreHorizontal,
  Pencil,
  Pin,
  PinOff,
  Plus,
  Search,
  Trash2,
  X,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'
import { persistedUiParam } from '@/frontend/pages/Dashboard/AgentChats/runtime/assistantUiToggle'
import { exportSessionUrl } from '@/frontend/pages/Dashboard/AgentChats/api'
import { fdeProjectPath, fdeRoutes } from '@/frontend/features/Fde/routes'
import type {
  AgentChatProject,
  AgentChatSessionSummary,
} from '@/frontend/pages/Dashboard/AgentChats/types'
import {
  AGENT_CHAT_DRAFT_EVENT,
  hasComposerDraft,
} from '@/frontend/features/AgentChats/assistant-ui/composerDrafts'
import { ParallelWorkLink } from '@/frontend/features/AgentChats/assistant-ui/components/ParallelWorkLink'
import type { SharedProps } from '@/frontend/types/inertia'

// Shared grouped session list (extracted from the former AssistantSidebar,
// SZ-1491). Rendered in the dashboard sidebar's Chat mode
// (SidebarConversationsPanel, SZ-1504). Date-grouped single-line threads with a
// hover ⋯ menu (rename / copy link / open).

export interface AssistantSessionListProps {
  activeSessionId: string | null
  isNewConversation: boolean
  sessions: AgentChatSessionSummary[]
  projects?: AgentChatProject[]
  workerId: string
  // The worker-first shell (SZ-1603, B.2) has its own fixed "Nova conversa"
  // above the agent list; false hides the internal one to avoid duplicating it.
  showNewConversation?: boolean
  newConversationDisabled?: boolean
  // Busca simples (Iteração 3): na aba Chat do portal a lista é a completa do
  // agente, então uma busca por título ajuda a achar conversas antigas. Off por
  // padrão — os recortes curtos (sidebar viva) não precisam.
  searchable?: boolean
  onNewConversation: () => void
  onRenameSession: (sessionId: string, title: string) => void
  onPinSession: (sessionId: string, pinned: boolean) => void
  onArchiveSession: (sessionId: string) => void
  onDeleteSession: (sessionId: string) => void
  onMoveSessionToProject?: (sessionId: string, projectId: string | null) => void
  onCreateProject?: (name: string) => void
  onRenameProject?: (projectId: string, name: string) => void
  onDeleteProject?: (projectId: string) => void
  onNavigate?: () => void
}

// Date buckets (B.2): Hoje / Ontem / Esta semana / Anteriores. "Esta semana"
// keeps the previous rolling 7-day cutoff (minus the days already covered by
// Hoje/Ontem) — no calendar-week jumps on Mondays.
export const GROUP_ORDER = ['Hoje', 'Ontem', 'Esta semana', 'Anteriores'] as const
export type GroupLabel = (typeof GROUP_ORDER)[number]

// Exported so the worker-first sidebar's light list (Iteração 1, I3.1) groups
// fetched sessions with the exact same buckets as this live list.
export function groupLabelFor(iso: string | null): GroupLabel {
  if (!iso) return 'Anteriores'
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return 'Anteriores'

  const now = new Date()
  const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const startYesterday = new Date(startToday)
  startYesterday.setDate(startYesterday.getDate() - 1)
  const startWeek = new Date(startToday)
  startWeek.setDate(startWeek.getDate() - 7)

  if (date >= startToday) return 'Hoje'
  if (date >= startYesterday) return 'Ontem'
  if (date >= startWeek) return 'Esta semana'
  return 'Anteriores'
}

export function sessionHref(workerId: string, sessionId: string): string {
  // Abrir uma conversa = a aba Chat do portal com aquele thread (Iteração 3,
  // B/C): /dashboard/digital_workers/:id/chat?session_id=X. Carrega o
  // ?ui=legacy persistido para manter o usuário no chat legado (incl.
  // copiar-link / abrir em nova aba); assistant-ui é o default e não precisa
  // do param. (A rota antiga /dashboard/agent_chats 302 pra cá.)
  const params = new URLSearchParams({ session_id: sessionId })
  const ui = persistedUiParam()
  if (ui) params.set('ui', ui)
  return `/dashboard/digital_workers/${workerId}/chat?${params.toString()}`
}

interface SessionGroup {
  key: string
  label: string
  kind: 'pinned' | 'project' | 'fde_project' | 'date'
  project?: AgentChatProject
  fdeProject?: { id: string; name: string }
  sessions: AgentChatSessionSummary[]
}

type PendingDeletion =
  | { kind: 'session'; id: string; name: string }
  | { kind: 'project'; id: string; name: string }

export function compactDateLabel(iso: string | null): string {
  if (!iso) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''

  const now = new Date()
  const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const startDate = new Date(date.getFullYear(), date.getMonth(), date.getDate())
  const dayDifference = Math.round((startToday.getTime() - startDate.getTime()) / 86_400_000)

  if (dayDifference === 0) return 'Hoje'
  if (dayDifference === 1) return 'Ontem'
  if (dayDifference > 1 && dayDifference < 7) {
    return new Intl.DateTimeFormat('pt-BR', { weekday: 'short' })
      .format(date)
      .replace('.', '')
  }

  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    ...(date.getFullYear() === now.getFullYear() ? {} : { year: '2-digit' as const }),
  }).format(date)
}

function groupSessions(
  sessions: AgentChatSessionSummary[],
  projects: AgentChatProject[],
): SessionGroup[] {
  const groups: SessionGroup[] = []
  for (const project of projects) {
    groups.push({
      key: `project:${project.id}`,
      label: project.name,
      kind: 'project',
      project,
      sessions: sessions.filter((session) => session.project_id === project.id),
    })
  }

  const fdeProjects = new Map<string, string>()
  for (const session of sessions) {
    if (!session.fde_project_id || fdeProjects.has(session.fde_project_id)) continue

    fdeProjects.set(session.fde_project_id, session.fde_project_name || 'Projeto sem nome')
  }
  for (const [id, name] of fdeProjects) {
    groups.push({
      key: `fde-project:${id}`,
      label: name,
      kind: 'fde_project',
      fdeProject: { id, name },
      sessions: sessions.filter((session) => session.fde_project_id === id),
    })
  }

  const pinned = sessions.filter((session) => session.pinned)
  if (pinned.length > 0) {
    groups.push({ key: 'pinned', label: 'Fixadas', kind: 'pinned', sessions: pinned })
  }

  const buckets = new Map<GroupLabel, AgentChatSessionSummary[]>()
  // Personal chat folders remain an additional organization axis and continue
  // appearing in recents. FDE project conversations live only under their
  // client project so shared work is not duplicated in the same sidebar.
  for (const session of sessions.filter((item) => !item.pinned && !item.fde_project_id)) {
    const label = groupLabelFor(session.last_message_at || session.created_at)
    const bucket = buckets.get(label) ?? []
    bucket.push(session)
    buckets.set(label, bucket)
  }
  for (const label of GROUP_ORDER.filter((item) => buckets.has(item))) {
    groups.push({ key: `date:${label}`, label, kind: 'date', sessions: buckets.get(label)! })
  }

  return groups
}

function SessionOptions({
  href,
  session,
  visible,
  onRename,
  onPin,
  onArchive,
  onDelete,
  projects,
  onMove,
  canManage,
}: {
  href: string
  session: AgentChatSessionSummary
  visible: boolean
  onRename: () => void
  onPin: () => void
  onArchive: () => void
  onDelete: () => void
  projects: AgentChatProject[]
  onMove?: (projectId: string | null) => void
  canManage: boolean
}) {
  const absoluteUrl = typeof window !== 'undefined' ? new URL(href, window.location.origin).toString() : href

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Opções da conversa"
          // Visibility is driven by JS hover state — Tailwind group-hover did not
          // resolve here. The open menu (data-state=open) keeps it shown too.
          className={cn(
            'flex size-6 items-center justify-center rounded-md text-muted-foreground transition hover:bg-card hover:text-foreground focus-visible:opacity-100 data-[state=open]:opacity-100',
            visible ? 'opacity-100' : 'opacity-0',
          )}
          onClick={(event) => event.preventDefault()}
        >
          <MoreHorizontal aria-hidden="true" className="size-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52">
        {canManage && (
          <>
            <DropdownMenuItem onClick={onPin}>
              {session.pinned ? <PinOff aria-hidden="true" className="size-4" /> : <Pin aria-hidden="true" className="size-4" />}
              {session.pinned ? 'Desafixar' : 'Fixar'}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={onRename}>
              <Pencil aria-hidden="true" className="size-4" />
              Renomear
            </DropdownMenuItem>
            {onMove && (
              <DropdownMenuSub>
                <DropdownMenuSubTrigger className="whitespace-nowrap">
                  <FolderInput aria-hidden="true" className="size-4" />
                  Mover para projeto
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent className="w-52">
                  <DropdownMenuLabel className="text-xs text-muted-foreground">Projetos</DropdownMenuLabel>
                  {session.project_id && (
                    <DropdownMenuItem onClick={() => onMove(null)}>Sem projeto</DropdownMenuItem>
                  )}
                  {projects.map((project) => (
                    <DropdownMenuItem
                      key={project.id}
                      disabled={session.project_id === project.id}
                      onClick={() => onMove(project.id)}
                    >
                      <FolderClosed aria-hidden="true" className="size-4" />
                      <span className="truncate">{project.name}</span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuSubContent>
              </DropdownMenuSub>
            )}
            <DropdownMenuSeparator />
          </>
        )}
        <DropdownMenuItem onClick={() => void navigator.clipboard?.writeText(absoluteUrl)}>
          <Link2 aria-hidden="true" className="size-4" />
          Copiar link
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => window.open(href, '_blank', 'noopener')}>
          <ExternalLink aria-hidden="true" className="size-4" />
          Abrir em nova aba
        </DropdownMenuItem>
        <DropdownMenuItem
          className="whitespace-nowrap"
          onClick={() => window.location.assign(exportSessionUrl(session.id))}
        >
          <Download aria-hidden="true" className="size-4" />
          Exportar Markdown
        </DropdownMenuItem>
        {canManage && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onArchive}>
              <Archive aria-hidden="true" className="size-4" />
              Arquivar
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-destructive focus:text-destructive"
              onClick={onDelete}
            >
              <Trash2 aria-hidden="true" className="size-4" />
              Excluir
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export function AssistantSessionList({
  activeSessionId,
  isNewConversation,
  sessions,
  projects = [],
  workerId,
  showNewConversation = true,
  newConversationDisabled = false,
  searchable = false,
  onNavigate,
  onNewConversation,
  onRenameSession,
  onPinSession,
  onArchiveSession,
  onDeleteSession,
  onMoveSessionToProject,
  onCreateProject,
  onRenameProject,
  onDeleteProject,
}: AssistantSessionListProps) {
  const [query, setQuery] = useState('')
  const trimmedQuery = query.trim().toLowerCase()
  const filteredSessions = useMemo(() => {
    if (!searchable || !trimmedQuery) return sessions
    return sessions.filter((session) => (
      session.title.toLowerCase().includes(trimmedQuery) ||
      session.fde_project_name?.toLowerCase().includes(trimmedQuery)
    ))
  }, [searchable, trimmedQuery, sessions])
  const orderedProjects = useMemo(() => [...projects].sort((left, right) => (
    left.position - right.position || left.name.localeCompare(right.name, 'pt-BR', { sensitivity: 'base' })
  )), [projects])
  const groups = groupSessions(filteredSessions, orderedProjects)
  const [editingSessionKey, setEditingSessionKey] = useState<string | null>(null)
  const [draftTitle, setDraftTitle] = useState('')
  const [hoveredSessionKey, setHoveredSessionKey] = useState<string | null>(null)
  const [creatingProject, setCreatingProject] = useState(false)
  const [projectDraft, setProjectDraft] = useState('')
  const [editingProjectId, setEditingProjectId] = useState<string | null>(null)
  const [projectTitleDraft, setProjectTitleDraft] = useState('')
  const [draggedSessionId, setDraggedSessionId] = useState<string | null>(null)
  const [dropProjectId, setDropProjectId] = useState<string | undefined>(undefined)
  const [expandedProjectIds, setExpandedProjectIds] = useState<Set<string>>(() => new Set(
    sessions.flatMap((session) => session.fde_project_id ? [session.fde_project_id] : []),
  ))
  const [pendingDeletion, setPendingDeletion] = useState<PendingDeletion | null>(null)
  const [, setDraftRevision] = useState(0)
  const sharedProps = usePage<SharedProps>().props
  const currentUserId = sharedProps.auth.user?.id ?? 'anonymous'
  const canManageFdeProjects = Boolean(
    sharedProps.auth.user?.is_admin || sharedProps.permissions?.can_manage_resources,
  )
  const dragPreviewRef = useRef<HTMLDivElement | null>(null)
  const pointerDragRef = useRef<{
    sessionId: string
    title: string
    dateLabel: string
    pointerId: number
    startX: number
    startY: number
    clientX: number
    clientY: number
    moved: boolean
    projectId?: string
  } | null>(null)
  const suppressSessionClickRef = useRef<string | null>(null)
  const requestedBackgroundRunId = typeof window === 'undefined'
    ? null
    : new URLSearchParams(window.location.search).get('background_run_id')

  useEffect(() => {
    const refreshDraftIndicators = () => setDraftRevision((current) => current + 1)
    window.addEventListener(AGENT_CHAT_DRAFT_EVENT, refreshDraftIndicators)
    window.addEventListener('storage', refreshDraftIndicators)
    return () => {
      window.removeEventListener(AGENT_CHAT_DRAFT_EVENT, refreshDraftIndicators)
      window.removeEventListener('storage', refreshDraftIndicators)
    }
  }, [])

  function startRename(session: AgentChatSessionSummary, groupKey: string) {
    setEditingSessionKey(`${groupKey}:${session.id}`)
    setDraftTitle(session.title)
  }

  function commitRename(sessionId: string) {
    const title = draftTitle.trim()
    setEditingSessionKey(null)
    if (title) onRenameSession(sessionId, title)
  }

  function handleRenameKeyDown(event: KeyboardEvent<HTMLInputElement>, sessionId: string) {
    if (event.key === 'Enter') {
      event.preventDefault()
      commitRename(sessionId)
    } else if (event.key === 'Escape') {
      event.preventDefault()
      setEditingSessionKey(null)
    }
  }

  function createProject() {
    const name = projectDraft.trim()
    if (!name || !onCreateProject) return
    onCreateProject(name)
    setProjectDraft('')
    setCreatingProject(false)
  }

  function commitProjectRename(projectId: string) {
    const name = projectTitleDraft.trim()
    setEditingProjectId(null)
    if (name) onRenameProject?.(projectId, name)
  }

  function confirmDeletion() {
    if (!pendingDeletion) return

    if (pendingDeletion.kind === 'session') onDeleteSession(pendingDeletion.id)
    else onDeleteProject?.(pendingDeletion.id)
    setPendingDeletion(null)
  }

  function projectAtPoint(clientX: number, clientY: number): string | undefined {
    const project = document
      .elementFromPoint(clientX, clientY)
      ?.closest<HTMLElement>('[data-project-drop-id]')
    return project?.dataset.projectDropId
  }

  function beginSessionPointerDrag(
    event: ReactPointerEvent<HTMLElement>,
    session: AgentChatSessionSummary,
  ) {
    if (
      !onMoveSessionToProject ||
      session.fde_project_id ||
      event.pointerType === 'touch' ||
      event.button !== 0
    ) return

    pointerDragRef.current = {
      sessionId: session.id,
      title: session.title,
      dateLabel: compactDateLabel(session.last_message_at || session.created_at),
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      clientX: event.clientX,
      clientY: event.clientY,
      moved: false,
    }
    event.currentTarget.setPointerCapture(event.pointerId)
  }

  function positionDragPreview() {
    const drag = pointerDragRef.current
    const preview = dragPreviewRef.current
    if (!drag || !preview) return

    preview.style.transform = `translate3d(${drag.clientX + 12}px, ${drag.clientY + 10}px, 0)`
    preview.style.visibility = 'visible'
  }

  function moveSessionPointerDrag(event: ReactPointerEvent<HTMLElement>) {
    const drag = pointerDragRef.current
    if (!drag || drag.pointerId !== event.pointerId) return

    const distance = Math.hypot(event.clientX - drag.startX, event.clientY - drag.startY)
    if (!drag.moved && distance < 6) return

    event.preventDefault()
    drag.moved = true
    drag.clientX = event.clientX
    drag.clientY = event.clientY
    drag.projectId = projectAtPoint(event.clientX, event.clientY)
    setDraggedSessionId(drag.sessionId)
    setDropProjectId(drag.projectId)
    requestAnimationFrame(positionDragPreview)
  }

  function finishSessionPointerDrag(event: ReactPointerEvent<HTMLElement>) {
    const drag = pointerDragRef.current
    if (!drag || drag.pointerId !== event.pointerId) return

    if (drag.moved) {
      event.preventDefault()
      suppressSessionClickRef.current = drag.sessionId
      if (drag.projectId) {
        setExpandedProjectIds((current) => new Set(current).add(drag.projectId!))
        onMoveSessionToProject?.(drag.sessionId, drag.projectId)
      }
    }

    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
    pointerDragRef.current = null
    dragPreviewRef.current = null
    setDraggedSessionId(null)
    setDropProjectId(undefined)
  }

  return (
    <div className="flex h-full min-h-0 flex-col p-2">
      {showNewConversation && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="w-full justify-start gap-2 rounded-lg font-normal text-foreground hover:bg-card/70"
          data-testid="agent-chat-new-conversation"
          disabled={newConversationDisabled}
          onClick={() => {
            onNewConversation()
            onNavigate?.()
          }}
        >
          <Plus className="size-4" />
          Nova conversa
          {hasComposerDraft(currentUserId, workerId, null) && (
            <span
              aria-label="Rascunho salvo"
              title="Rascunho salvo"
              className="ml-auto size-1.5 rounded-full bg-agent"
            />
          )}
        </Button>
      )}

      {searchable && sessions.length > 0 && (
        <div className="relative mt-1 px-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-3.5 -translate-y-1/2 text-faint-foreground" />
          <input
            type="text"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Buscar conversas…"
            aria-label="Buscar conversas"
            data-testid="agent-chat-session-search"
            className="w-full rounded-lg border border-border/80 bg-card py-1.5 pl-8 pr-8 text-[13px] text-foreground placeholder:text-faint-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
          {query && (
            <button
              type="button"
              aria-label="Limpar busca"
              onClick={() => setQuery('')}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-faint-foreground hover:text-foreground"
            >
              <X className="size-3.5" />
            </button>
          )}
        </div>
      )}

      {onCreateProject && (
        <div className="mt-2 px-1" data-testid="agent-chat-projects-section">
          <div className="flex h-7 items-center justify-between px-2">
            <span className="text-[10.5px] font-medium tracking-[0.04em] text-faint-foreground">Pastas do chat</span>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label="Criar projeto"
              title="Criar projeto"
              data-testid="agent-chat-project-create"
              onClick={() => setCreatingProject(true)}
              className="size-6 text-muted-foreground"
            >
              <Plus aria-hidden="true" className="size-3.5" />
            </Button>
          </div>
          {creatingProject && (
            <input
              autoFocus
              value={projectDraft}
              maxLength={80}
              onChange={(event) => setProjectDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault()
                  createProject()
                }
                if (event.key === 'Escape') setCreatingProject(false)
              }}
              onBlur={() => setCreatingProject(false)}
              aria-label="Nome do projeto"
              placeholder="Nome do projeto"
              data-testid="agent-chat-project-name-input"
              className="w-full rounded-lg border border-border/70 bg-card px-2.5 py-1.5 text-[13px] text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          )}
        </div>
      )}

      <div className="chat-session-scrollbar mt-1 min-h-0 flex-1 overflow-y-auto overscroll-contain pr-0.5">
        <div className="space-y-3 py-1" data-testid="agent-chat-session-list">
          {sessions.length === 0 && orderedProjects.length === 0 ? (
            // Mesma copy do estado vazio da lista leve do shell (Iteração 1,
            // I3.1): um vazio só, onde quer que a lista apareça.
            <p className="px-3 py-8 text-center text-sm text-muted-foreground">Sem conversas ainda.</p>
          ) : filteredSessions.length === 0 && trimmedQuery ? (
            <p className="px-3 py-8 text-center text-sm text-muted-foreground">Nenhuma conversa com esse termo.</p>
          ) : (
            groups.map((group, groupIndex) => (
              <Fragment key={group.key}>
              {group.kind === 'fde_project' && groups[groupIndex - 1]?.kind !== 'fde_project' && (
                <p className="px-2 pb-0.5 pt-2 text-[10.5px] font-medium tracking-[0.04em] text-faint-foreground">
                  Projetos da Workstation
                </p>
              )}
              {group.kind === 'date' && groups[groupIndex - 1]?.kind !== 'date' && (
                <p className="px-2 pb-0.5 pt-2 text-[10.5px] font-medium tracking-[0.04em] text-faint-foreground">
                  Recentes
                </p>
              )}
              <div
                className="space-y-px"
                data-project-drop-id={group.project?.id}
                data-testid={group.project
                  ? `agent-chat-project-${group.project.id}`
                  : group.fdeProject
                    ? `agent-chat-fde-project-${group.fdeProject.id}`
                    : undefined}
              >
                {group.project || group.fdeProject ? (
                  <div className={cn(
                    'group/project flex min-h-7 items-center gap-1 rounded-md px-2 transition-colors',
                    group.project && dropProjectId === group.project.id && 'bg-brand/8 ring-1 ring-brand/25',
                  )}>
                    {group.project && editingProjectId === group.project.id ? (
                      <>
                        <ChevronRight aria-hidden="true" className="size-3.5 shrink-0 text-faint-foreground" />
                        <FolderClosed aria-hidden="true" className="size-3.5 shrink-0 text-muted-foreground" />
                        <input
                          autoFocus
                          value={projectTitleDraft}
                          maxLength={80}
                          onChange={(event) => setProjectTitleDraft(event.target.value)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter') commitProjectRename(group.project!.id)
                            if (event.key === 'Escape') setEditingProjectId(null)
                          }}
                          onBlur={() => commitProjectRename(group.project!.id)}
                          aria-label="Renomear projeto"
                          className="min-w-0 flex-1 bg-transparent text-[12.5px] text-foreground outline-none"
                        />
                        <span className="text-[10px] tabular-nums text-faint-foreground">{group.sessions.length}</span>
                      </>
                    ) : (
                      <button
                        type="button"
                        aria-expanded={expandedProjectIds.has((group.project || group.fdeProject)!.id)}
                        aria-controls={`${group.kind === 'fde_project' ? 'agent-chat-fde-project' : 'agent-chat-project'}-content-${(group.project || group.fdeProject)!.id}`}
                        onClick={() => setExpandedProjectIds((current) => {
                          const next = new Set(current)
                          const projectId = (group.project || group.fdeProject)!.id
                          if (next.has(projectId)) next.delete(projectId)
                          else next.add(projectId)
                          return next
                        })}
                        className="flex min-w-0 flex-1 items-center gap-1.5 rounded text-left outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        <ChevronRight
                          aria-hidden="true"
                          className={cn(
                            'size-3.5 shrink-0 text-faint-foreground transition-transform duration-150',
                            expandedProjectIds.has((group.project || group.fdeProject)!.id) && 'rotate-90',
                          )}
                        />
                        {group.fdeProject
                          ? <BriefcaseBusiness aria-hidden="true" className="size-3.5 shrink-0 text-brand" />
                          : <FolderClosed aria-hidden="true" className="size-3.5 shrink-0 text-muted-foreground" />}
                        <span className="min-w-0 flex-1 truncate text-[12.5px] text-muted-foreground">{group.label}</span>
                        <span className="text-[10px] tabular-nums text-faint-foreground">{group.sessions.length}</span>
                      </button>
                    )}
                    {group.fdeProject && (
                      <Link
                        href={fdeProjectPath(fdeRoutes.workspace, group.fdeProject.id)}
                        aria-label={`Abrir projeto ${group.label}`}
                        title="Abrir projeto"
                        className="flex size-5 items-center justify-center rounded text-faint-foreground transition hover:bg-card hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        <ExternalLink aria-hidden="true" className="size-3" />
                      </Link>
                    )}
                    {group.project && (onRenameProject || onDeleteProject) && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            type="button"
                            aria-label={`Opções do projeto ${group.label}`}
                            className="flex size-5 items-center justify-center rounded text-muted-foreground opacity-0 hover:bg-card group-hover/project:opacity-100 focus-visible:opacity-100 data-[state=open]:opacity-100"
                          >
                            <MoreHorizontal aria-hidden="true" className="size-3.5" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-40">
                          {onRenameProject && (
                            <DropdownMenuItem onClick={() => {
                              setEditingProjectId(group.project!.id)
                              setProjectTitleDraft(group.project!.name)
                            }}>
                              <Pencil aria-hidden="true" className="size-4" />
                              Renomear
                            </DropdownMenuItem>
                          )}
                          {onDeleteProject && (
                            <DropdownMenuItem
                              className="text-destructive focus:text-destructive"
                              onClick={() => setPendingDeletion({
                                kind: 'project',
                                id: group.project!.id,
                                name: group.project!.name,
                              })}
                            >
                              <Trash2 aria-hidden="true" className="size-4" />
                              Excluir projeto
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                ) : (
                  <p className="flex items-center gap-1.5 px-2 pb-0.5 pt-1 text-[10.5px] tracking-[0.04em] text-faint-foreground">
                    {group.label}
                  </p>
                )}
                <div
                  id={group.project
                    ? `agent-chat-project-content-${group.project.id}`
                    : group.fdeProject
                      ? `agent-chat-fde-project-content-${group.fdeProject.id}`
                      : undefined}
                  className="space-y-px"
                >
                {group.project && expandedProjectIds.has(group.project.id) && group.sessions.length === 0 && (
                  <p className="px-7 py-1 text-[11px] text-faint-foreground">
                    {draggedSessionId ? 'Solte a conversa aqui' : 'Projeto vazio'}
                  </p>
                )}
                {(
                  (!group.project && !group.fdeProject) ||
                  expandedProjectIds.has((group.project || group.fdeProject)!.id)
                ) && group.sessions.map((session) => {
                  const active = session.id === activeSessionId && !isNewConversation
                  const href = sessionHref(workerId, session.id)
                  const occurrenceKey = `${group.key}:${session.id}`

                  if (editingSessionKey === occurrenceKey) {
                    return (
                      <input
                        key={session.id}
                        autoFocus
                        value={draftTitle}
                        onChange={(event) => setDraftTitle(event.target.value)}
                        onKeyDown={(event) => handleRenameKeyDown(event, session.id)}
                        onBlur={() => commitRename(session.id)}
                        aria-label="Renomear conversa"
                        className="w-full rounded-lg bg-background px-3 py-2 text-sm text-foreground outline-none ring-2 ring-brand/40"
                      />
                    )
                  }

                  return (
                    <Fragment key={session.id}>
                      <div
                        data-draggable={onMoveSessionToProject && !session.fde_project_id ? 'true' : undefined}
                        title={onMoveSessionToProject && !session.fde_project_id
                          ? 'Arraste para mover esta conversa para um projeto'
                          : undefined}
                        onMouseEnter={() => setHoveredSessionKey(occurrenceKey)}
                        onMouseLeave={() => setHoveredSessionKey(null)}
                        className={cn(
                          'flex cursor-default items-center gap-1 rounded-lg pr-1 transition-colors duration-150',
                          draggedSessionId === session.id && 'opacity-45',
                          active
                            ? 'bg-card text-foreground shadow-xs ring-1 ring-border/55'
                            : 'text-muted-foreground hover:bg-accent/60 hover:text-foreground',
                        )}
                      >
                        <Link
                          href={href}
                          preserveScroll
                          draggable={false}
                          onPointerDown={(event) => beginSessionPointerDrag(event, session)}
                          onPointerMove={moveSessionPointerDrag}
                          onPointerUp={finishSessionPointerDrag}
                          onPointerCancel={finishSessionPointerDrag}
                          data-testid={group.project
                            ? `agent-chat-project-session-link-${session.id}`
                            : group.fdeProject
                              ? `agent-chat-fde-project-session-link-${session.id}`
                              : `agent-chat-session-link-${session.id}`}
                          onClick={(event) => {
                            if (suppressSessionClickRef.current === session.id) {
                              event.preventDefault()
                              suppressSessionClickRef.current = null
                              return
                            }
                            onNavigate?.()
                          }}
                          className={cn(
                            'flex min-w-0 flex-1 touch-pan-y items-center gap-2 py-[5px] text-[13px]',
                            group.project || group.fdeProject ? 'pl-7' : 'pl-2',
                            onMoveSessionToProject && !session.fde_project_id && 'cursor-grab select-none active:cursor-grabbing',
                          )}
                        >
                          <span className="min-w-0 flex-1 truncate">{session.title}</span>
                          {hasComposerDraft(currentUserId, workerId, session.id) && (
                            <span
                              aria-label="Rascunho salvo"
                              title="Rascunho salvo"
                              className="size-1.5 shrink-0 rounded-full bg-agent"
                            />
                          )}
                          <time
                            dateTime={session.last_message_at || session.created_at}
                            className="shrink-0 text-[10px] tabular-nums text-faint-foreground"
                          >
                            {compactDateLabel(session.last_message_at || session.created_at)}
                          </time>
                        </Link>
                        <SessionOptions
                          href={href}
                          session={session}
                          visible={hoveredSessionKey === occurrenceKey}
                          projects={orderedProjects}
                          canManage={!session.fde_project_id || canManageFdeProjects}
                          onMove={onMoveSessionToProject && !session.fde_project_id
                            ? (projectId) => onMoveSessionToProject(session.id, projectId)
                            : undefined}
                          onRename={() => startRename(session, group.key)}
                          onPin={() => onPinSession(session.id, !session.pinned)}
                          onArchive={() => onArchiveSession(session.id)}
                          onDelete={() => setPendingDeletion({
                            kind: 'session',
                            id: session.id,
                            name: session.title,
                          })}
                        />
                      </div>

                      {(session.parallel_work || []).map((work) => (
                        <ParallelWorkLink
                          key={work.id}
                          work={work}
                          workerId={workerId}
                          sessionId={session.id}
                          nested={Boolean(group.project || group.fdeProject)}
                          selected={requestedBackgroundRunId === work.run_id}
                          onNavigate={onNavigate}
                          testId={group.project
                            ? `agent-chat-project-parallel-work-${work.id}`
                            : group.fdeProject
                              ? `agent-chat-fde-project-parallel-work-${work.id}`
                            : `agent-chat-parallel-work-${work.id}`}
                        />
                      ))}
                    </Fragment>
                  )
                })}
                </div>
              </div>
              </Fragment>
            ))
          )}
        </div>
      </div>

      {draggedSessionId && typeof document !== 'undefined' && createPortal(
        <div
          ref={(element) => {
            dragPreviewRef.current = element
            positionDragPreview()
          }}
          aria-hidden="true"
          data-testid="agent-chat-drag-preview"
          className="pointer-events-none fixed left-0 top-0 z-[100] flex w-56 max-w-[calc(100vw-2rem)] items-center gap-2 rounded-lg border border-border/80 bg-card/95 px-2.5 py-2 text-[13px] text-foreground shadow-lg backdrop-blur-sm invisible duration-100 animate-in fade-in-0 zoom-in-95 motion-reduce:animate-none"
        >
          <GripVertical aria-hidden="true" className="size-3.5 shrink-0 text-faint-foreground" />
          <span className="min-w-0 flex-1 truncate">
            {pointerDragRef.current?.title || sessions.find((session) => session.id === draggedSessionId)?.title}
          </span>
          <span className="shrink-0 text-[10px] tabular-nums text-faint-foreground">
            {pointerDragRef.current?.dateLabel}
          </span>
        </div>,
        document.body,
      )}

      <AlertDialog
        open={pendingDeletion !== null}
        onOpenChange={(open) => {
          if (!open) setPendingDeletion(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pendingDeletion?.kind === 'project' ? 'Excluir projeto?' : 'Excluir conversa?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pendingDeletion?.kind === 'project'
                ? `O projeto “${pendingDeletion.name}” será excluído. As conversas continuarão no histórico recente.`
                : `A conversa “${pendingDeletion?.name ?? ''}” será excluída permanentemente. Esta ação não pode ser desfeita.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={confirmDeletion}
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
